import {
  type FulfillResponse,
  FulfillResponseEnum,
} from "@b2bportal/purchase-api";
import {
  type CartFulfillError,
  CartFulfillErrorModal,
  CartFulfillState,
} from "@checkout/types";
import { pollFulfill as defaultPollFulfill } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";
import {
  PollingResult,
  PollingType,
  endPollingPerformance,
  startPollingPerformance,
} from "@hopper-b2b/utilities";
import type { CartContext } from "../../context";
import { getFulfillCipherText } from "../selectors";

export interface PollFulfillServiceOptions {
  maxPendingPolls?: number;
  pollFulfillFn?: typeof defaultPollFulfill;
  waitFn?: (delayMs: number) => Promise<void>;
}

export const createPollFulfillService = ({
  maxPendingPolls,
  pollFulfillFn = defaultPollFulfill,
  waitFn = wait,
}: PollFulfillServiceOptions = {}) => {
  return async (
    context: CartContext,
    _event: unknown
  ): Promise<FulfillResponse | CartFulfillError> => {
    const fulFillCipherText = getFulfillCipherText({ context });
    const cartFulfillError: Omit<CartFulfillError, "type"> = {
      failedAt: CartFulfillState.polling,
    };

    const startPerformance = startPollingPerformance();
    try {
      if (!fulFillCipherText) {
        const error = {
          ...cartFulfillError,
          type: CartFulfillErrorModal.NoCipherText,
        } as CartFulfillError;

        throw error;
      }

      const delayTimes = [1000];
      let pollComplete = false;
      let index = 0;
      let pendingPolls = 0;
      let error;

      while (!pollComplete) {
        const currentDelayTime = delayTimes[index];
        if (index !== delayTimes.length - 1) {
          index++;
        }

        await waitFn(currentDelayTime);

        const response: FulfillResponse = await withRetry(
          pollFulfillFn,
          waitFn
        )(fulFillCipherText);

        switch (response.FulfillResponse) {
          case FulfillResponseEnum.Failure: {
            pollComplete = true;
            if (response.errors.length > 0) {
              const hasKcpErrors = response.errors.some(
                (error) => "KcpError" in error
              );
              const hasRewardsPaymentErrors = response.errors.some(
                (error) => "RewardsPaymentError" in error
              );
              if (hasKcpErrors) {
                error = {
                  ...cartFulfillError,
                  type: CartFulfillErrorModal.KcpFailure,
                  data: response.errors,
                };
              } else if (hasRewardsPaymentErrors) {
                // handle reward payment error
                error = {
                  ...cartFulfillError,
                  type: CartFulfillErrorModal.RewardsPaymentFailure,
                  data: response.errors,
                };
              } else {
                error = {
                  ...cartFulfillError,
                  type: CartFulfillErrorModal.FailedPolling,
                  data: response.errors,
                } as CartFulfillError;
              }

              throw error;
            } else {
              const error = {
                ...cartFulfillError,
                type: CartFulfillErrorModal.FailedPollingNoErrorCode,
              } as CartFulfillError;

              throw error;
            }
          }
          case FulfillResponseEnum.Pending:
            if (maxPendingPolls !== undefined) {
              pendingPolls++;
              if (pendingPolls >= maxPendingPolls) {
                endPollingPerformance(
                  startPerformance,
                  PollingType.FULFILL,
                  PollingResult.SUCCESS
                );
                pollComplete = true;
                return response;
              }
            }
            continue;
          case FulfillResponseEnum.Success: {
            endPollingPerformance(
              startPerformance,
              PollingType.FULFILL,
              PollingResult.SUCCESS
            );

            pollComplete = true;
            return response;
          }
          case FulfillResponseEnum.PendingInteractivePaymentAuth: {
            if (context.cartFulfill.completeInteractive.results.length > 0) {
              if (maxPendingPolls !== undefined) {
                pendingPolls++;
                if (pendingPolls >= maxPendingPolls) {
                  endPollingPerformance(
                    startPerformance,
                    PollingType.FULFILL,
                    PollingResult.SUCCESS
                  );
                  pollComplete = true;
                  return response;
                }
              }
              continue;
            }
            endPollingPerformance(
              startPerformance,
              PollingType.FULFILL,
              PollingResult.CHALLENGE
            );

            pollComplete = true;
            return response;
          }
          // PendingExtended is the state where the backend keeps polling the results but it could take
          // longer than a few seconds. There is nothing more for this service to do, so it should just
          // end polling with a success response, which would lead to booking confirmation page in most
          // state machines.
          // It doesn't return anything useful for FE to use to show the users, so the FE should show
          // something like "Expect an email, and come back later in My Trips to see results".
          // See
          case FulfillResponseEnum.PendingExtended: {
            endPollingPerformance(
              startPerformance,
              PollingType.FULFILL,
              PollingResult.PENDING_EXTENDED
            );

            pollComplete = true;
            return response;
          }
        }
      }
    } catch (e) {
      const error = {
        ...cartFulfillError,
        type: CartFulfillErrorModal.Unknown,
        data: e,
      };

      endPollingPerformance(
        startPerformance,
        PollingType.FULFILL,
        PollingResult.FAILURE,
        {
          error_type: error?.type,
          error,
        }
      );

      if (isCartFulfillError(e)) throw e;
      throw error;
    }
  };
};

export const pollFulfillService = createPollFulfillService();

const isCartFulfillError = (
  error: CartFulfillError | unknown
): error is CartFulfillError => (error as CartFulfillError).type !== undefined;

function withRetry<P extends any[], R>(
  service: (...args: P) => Promise<R>,
  waitFn: (delayMs: number) => Promise<void>,
  initialDelay = 1000,
  multiplier = 2,
  maxRetries = 5
) {
  let delay = initialDelay;
  let retries = 0;
  return async (...args: P) => {
    while (true) {
      try {
        const res = await service(...args);
        return res;
      } catch (err) {
        if (retries > maxRetries) {
          logger()?.error(`Poll failed after retrying ${maxRetries} times`, {
            error: err,
          });
          throw err;
        }
        await waitFn(delay);
        retries++;
        delay *= multiplier;
      }
    }
  };
}

function wait(delayMs: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, delayMs));
}
